import { CheckCircleOutlined, PlusOutlined, UserOutlined } from '@ant-design/icons'
import { Badge, COLORS, Layout, Typography } from '@community_dev/pixels'
import { ApiError } from '@community_dev/requests'
import { RoleGroup } from '@community_dev/types/lib/api/v1/Roles'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, Button, Col, Input, List, Row, Skeleton, Space, Tag, Tooltip } from 'antd'
import { useState, type JSX } from 'react'
import { useParams } from 'react-router-dom'

import {
  capabilityPrefixesForRole,
  diffRole,
  groupCapabilitiesByPrefix,
  hasRoleChanges,
  RoleFields,
  roleFields,
} from '../roleChanges'

import { AddRoleCapabilityModal } from './AddRoleCapabilityModal'
import { ReviewRoleChangesModal } from './ReviewRoleChangesModal'

import { getRole, getRoleCapabilityCatalog, RoleVisibility, updateRole } from 'api/capabilities'
import { CAPABILITIES } from 'constants/capabilities'
import { QUERIES, STALE_TIME } from 'constants/queries'
import { useHasCapability } from 'hooks/useCapabilities'
import { useToastMessage } from 'hooks/useNotification'
import { formatDate } from 'utils/formatters'
import { getRoleLabel } from 'utils/roles'

const VISIBILITY_LABELS: Record<RoleVisibility, string> = {
  [RoleVisibility.ADMIN]: 'Admin role',
  [RoleVisibility.PRIVATE]: 'Private role',
  [RoleVisibility.PUBLIC]: 'Client role',
}

// history 4 decodes the path with decodeURI, which keeps reserved characters such as %26 and %2F encoded.
function decodeRoleName(routeParam: string): string {
  try {
    return decodeURIComponent(routeParam)
  } catch {
    return routeParam
  }
}

function errorDetails(error: unknown): string {
  if (error instanceof ApiError && error.errors) {
    return error.errors.map((e) => ('field' in e ? `${e.field}: ${e.message}` : e.detail || e.message)).join(' ')
  }
  return ''
}

export function Role(): JSX.Element {
  const roleName = decodeRoleName(useParams<{ name: string }>().name)
  const canEditRoles = useHasCapability(CAPABILITIES.GLOBAL.ROLE.WRITE)
  const canListCapabilities = useHasCapability(CAPABILITIES.GLOBAL.ADMIN.ALL)
  const queryClient = useQueryClient()
  const { showNotification } = useToastMessage()
  const [draft, setDraft] = useState<RoleFields | null>(null)
  const [isAddCapabilityModalOpen, setIsAddCapabilityModalOpen] = useState(false)
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false)

  const { data: role, isError } = useQuery([QUERIES.ROLE, roleName], () => getRole(roleName))
  const catalogPrefixes = role ? capabilityPrefixesForRole(role.visibility) : []
  const {
    data: capabilityCatalog = [],
    isInitialLoading: isCatalogLoading,
    isError: isCatalogError,
  } = useQuery([QUERIES.ROLE_CAPABILITY_CATALOG, ...catalogPrefixes], () => getRoleCapabilityCatalog(catalogPrefixes), {
    enabled: isAddCapabilityModalOpen,
    // Each failed request opens an error dialog, so a retry repeats every dialog.
    retry: false,
    staleTime: STALE_TIME.FIVE_MINUTES,
  })

  const { mutate: saveRole, isLoading: isSaving } = useMutation({
    mutationFn: (savedDraft: RoleFields) =>
      updateRole(roleName, {
        displayName: savedDraft.displayName,
        description: savedDraft.description,
        capabilityNames: savedDraft.capabilities.map((capability) => capability.name),
      }),
    onSuccess: async () => {
      await Promise.all(
        [QUERIES.ROLE, QUERIES.ROLES, QUERIES.ROLES_ADMINS].map((key) => queryClient.invalidateQueries([key])),
      )
      setIsReviewModalOpen(false)
      setDraft(null)
      showNotification('The role was saved.')
    },
    onError: (error) => {
      showNotification(`The role was not saved. ${errorDetails(error)}`, false)
    },
  })

  if (!role) {
    return (
      <Layout padding="48px">
        {isError ? (
          <Alert message={`The role “${roleName}” could not be loaded.`} showIcon type="error" />
        ) : (
          <Skeleton active />
        )}
      </Layout>
    )
  }

  const capabilities = draft ? draft.capabilities : role.capabilities
  const changes = draft ? diffRole(roleFields(role), draft) : null
  const addedNames = new Set((changes?.added ?? []).map((capability) => capability.name))
  const hasChanges = changes !== null && hasRoleChanges(changes)

  const startEditing = () => setDraft(roleFields(role))

  const removeCapability = (capabilityName: string) =>
    draft &&
    setDraft({
      ...draft,
      capabilities: draft.capabilities.filter((capability) => capability.name !== capabilityName),
    })

  return (
    <Layout padding="48px">
      {draft && isAddCapabilityModalOpen && (
        <AddRoleCapabilityModal
          capabilities={capabilityCatalog}
          existingCapabilities={draft.capabilities}
          isError={isCatalogError}
          isLoading={isCatalogLoading}
          onAdd={(capability) => setDraft({ ...draft, capabilities: [...draft.capabilities, capability] })}
          onClose={() => setIsAddCapabilityModalOpen(false)}
          roleVisibility={role.visibility}
        />
      )}
      {draft && changes && isReviewModalOpen && (
        <ReviewRoleChangesModal
          changes={changes}
          isSaving={isSaving}
          onCancel={() => setIsReviewModalOpen(false)}
          onConfirm={() => saveRole(draft)}
          role={role}
        />
      )}
      <Typography variant="overline">
        <UserOutlined /> {VISIBILITY_LABELS[role.visibility]}
      </Typography>
      <h1>
        {getRoleLabel(role)} {role.group === RoleGroup.PRIMARY && <Tag>Primary</Tag>}
        {role.protected && <Tag>Protected</Tag>}
      </h1>
      <Row gutter={100} style={{ marginBottom: '24px' }}>
        <Col>
          <Typography variant="caption1">Name</Typography>
          <Typography variant="body1">{role.name}</Typography>
        </Col>
        <Col>
          <Typography variant="caption1">Seats with this role</Typography>
          <Typography variant="body1">{role.seatMappingCount}</Typography>
        </Col>
        <Col>
          <Typography variant="caption1">Updated</Typography>
          <Typography variant="body1">{formatDate(role.updatedAt)}</Typography>
        </Col>
      </Row>
      {draft ? (
        <Space direction="vertical" style={{ width: '100%', marginBottom: '24px' }}>
          <label htmlFor="role-display-name">Display name</label>
          <Input
            id="role-display-name"
            onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
            value={draft.displayName}
          />
          <label htmlFor="role-description">Description</label>
          <Input.TextArea
            id="role-description"
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            value={draft.description}
          />
        </Space>
      ) : (
        <div style={{ marginBottom: '24px' }}>
          <Typography variant="caption1">Description</Typography>
          <Typography variant="body1">{role.description || '(no description)'}</Typography>
        </div>
      )}
      <Space style={{ marginBottom: '24px' }}>
        {draft ? (
          <>
            <Tooltip title={canListCapabilities ? undefined : 'You need global:admin:all to list capabilities.'}>
              <span>
                <Button
                  disabled={!canListCapabilities}
                  icon={<PlusOutlined />}
                  onClick={() => setIsAddCapabilityModalOpen(true)}
                  shape="round"
                >
                  Add Capability
                </Button>
              </span>
            </Tooltip>
            <Button disabled={!hasChanges} onClick={() => setIsReviewModalOpen(true)} shape="round" type="primary">
              Review changes
            </Button>
            <Button onClick={() => setDraft(null)} shape="round">
              Cancel
            </Button>
          </>
        ) : (
          <Tooltip title={canEditRoles ? undefined : 'You do not have permission to change roles.'}>
            <span>
              <Button disabled={!canEditRoles} onClick={startEditing} shape="round" type="primary">
                Edit role
              </Button>
            </span>
          </Tooltip>
        )}
      </Space>
      <Typography display="block" variant="caption1">
        Capabilities <Badge>{capabilities.length}</Badge>
      </Typography>
      {capabilities.length === 0 && (
        <Typography color={COLORS.SUBTEXT} variant="body2">
          This role has no capabilities.
        </Typography>
      )}
      {groupCapabilitiesByPrefix(capabilities).map(([prefix, group]) => (
        <List
          dataSource={group}
          header={<Typography variant="caption1">{prefix}</Typography>}
          key={prefix}
          renderItem={(capability) => (
            <List.Item
              actions={
                draft
                  ? [
                      <Button
                        aria-label={`Remove ${capability.name}`}
                        onClick={() => removeCapability(capability.name)}
                        shape="round"
                        size="small"
                      >
                        Remove
                      </Button>,
                    ]
                  : []
              }
            >
              <List.Item.Meta
                avatar={<CheckCircleOutlined />}
                description={capability.description || '(no description)'}
                title={
                  <>
                    {capability.name} {addedNames.has(capability.name) && <Tag color="green">Added</Tag>}
                  </>
                }
              />
            </List.Item>
          )}
          rowKey="name"
        />
      ))}
    </Layout>
  )
}
